import {
  WppButton,
  WppInlineMessage,
  WppListItem,
  WppModal,
  WppSelect,
  WppTypography,
} from '@wppopen/components-library-react'
import { CircleFlag } from 'react-circle-flags'
import clsx from 'clsx'
import appContext from 'context/appContext'
import { useContext, useMemo, useReducer, useState } from 'react'
import { renderEmptyState, renderSelectItem } from '../helpers'
import addRegionToProjectFormReducer, {
  type AddRegionToProjectMultiSelectField,
  type AddRegionToProjectSelectField,
  emptyAddRegionToProjectFormReducerState,
  addRegionToProjectFormHelperFunctions,
  defaultAddRegionToProjectErrorObject,
  initialRegionFormReducerState,
} from 'reducers/AddAndEditProjectRegionReducer/AddAndEditProjectRegionReducer'
import { ERROR_MESSAGES } from 'utils/constants'
import { getType } from 'utils'
import { getActiveRegionRetailers } from 'pages/ProjectView/utils'
import { uuidv4, type SelectChangeEventDetail } from '@wppopen/components-library'
import type { FEApiError } from 'utils/errorHandling'
import type { WppSelectCustomEvent } from '@wppopen/components-library/dist/types/components'
import type { ProjectsDetailResult, ProjectsRegionsCreatePayload } from '@contracts'
import type { ExtractElementType } from 'utils/utils.d'
import styles from '../Modal.module.scss'

interface AddAndEditProjectRegionProps {
  isModalOpen: boolean
  isApiLoading: boolean
  onCancel: () => void
  onSubmit: (data: ProjectsRegionsCreatePayload) => Promise<void>
  regionData?: ExtractElementType<ProjectsDetailResult['regions']>
}

export default function AddAndEditProjectRegion({
  isModalOpen,
  isApiLoading,
  onCancel,
  onSubmit,
  regionData,
}: AddAndEditProjectRegionProps) {
  // the multiple select component doesn't render properly and to fix that we need to rerender the component
  const [rerender, setRerender] = useState(uuidv4())
  const initialRegionFormState = useMemo(
    () => (!!regionData ? initialRegionFormReducerState(regionData) : emptyAddRegionToProjectFormReducerState),
    [regionData],
  )
  const { MANDATORY_FIELD } = ERROR_MESSAGES
  const { configApi } = useContext(appContext)
  const [errorMessage, setErrorMessage] = useState<string>()
  const { isLoading: isConfigLoading, data: configData } = configApi
  const configRegions = configData?.regions
  const configRetailers = configData?.retailers
  const [formReducerState, formReducerDispatch] = useReducer(addRegionToProjectFormReducer, initialRegionFormState)
  const { region_id, retailers } = formReducerState
  const { updateSelect, setSelect, handleError } = addRegionToProjectFormHelperFunctions
  // get the selected region based on the region_id value
  const selectedRegion = useMemo(() => {
    return configData?.regions?.find(({ _id }) => _id === region_id.value)
  }, [region_id.value, configData])
  // get the retailers based on the selected region
  const retailerOptions = useMemo(() => {
    return getActiveRegionRetailers(configData, selectedRegion)
  }, [configRetailers, selectedRegion?.retailers])

  // handle the select change event
  const handleSelect = (e: WppSelectCustomEvent<SelectChangeEventDetail>) => {
    const { value, name } = e.target
    const typeSafeInputFieldKey = name as AddRegionToProjectSelectField
    formReducerDispatch(updateSelect(typeSafeInputFieldKey, value))

    if (formReducerState[typeSafeInputFieldKey].error && !!value)
      formReducerDispatch(handleError(typeSafeInputFieldKey, undefined))
    else if (!formReducerState[typeSafeInputFieldKey].error && !value)
      formReducerDispatch(handleError(typeSafeInputFieldKey, ERROR_MESSAGES.MANDATORY_FIELD))
  }

  // handle the form submit event
  const handleSubmitForm = () => {
    setErrorMessage(undefined)
    const requiredFields = defaultAddRegionToProjectErrorObject.requiredField?.map(({ id }) => id) || []
    let isFormValid = true

    requiredFields.forEach(field => {
      const { value, error } = formReducerState[field]
      // if value is empty or there is an error message
      if (!value || !!error || !value.length) {
        // if value is NOT empty and there is an error message, remove the error message otherwise set the error message
        if (Array.isArray(value)) formReducerDispatch(handleError(field, !value.length ? MANDATORY_FIELD : undefined))
        else formReducerDispatch(handleError(field, !!value ? undefined : MANDATORY_FIELD))
        // if value is empty, make the form invalid
        if (!value || !value.length) return (isFormValid = false)
      }
    })

    if (!isFormValid) return

    //ProjectsRegionsCreatePayload
    const addRegionToProjectData: any = {
      region_id: region_id.value,
      retailers: retailers.value,
    }

    onSubmit(addRegionToProjectData).catch((err: FEApiError) => {
      const { errors, message } = err

      if (errors) {
        errors.forEach(({ path, message }) => {
          formReducerDispatch(
            handleError(path as AddRegionToProjectSelectField | AddRegionToProjectMultiSelectField, message),
          )
        })
      } else if (message) {
        setErrorMessage(message)
      }
    })
  }

  return (
    <WppModal
      open={isModalOpen}
      formConfig={{
        onSubmit: e => {
          e.preventDefault()
          handleSubmitForm()
        },
        onReset: () => onCancel(),
      }}
      className={clsx(styles.modal_reset_overflow, styles.modal)}
    >
      <WppTypography tag="h3" type="l-strong" slot="header">
        {!!regionData ? 'Edit' : 'Add'} region
      </WppTypography>

      <div slot="body">
        <WppSelect
          data-testid="region-select"
          name={getType<AddRegionToProjectSelectField>('region_id')}
          labelConfig={{ text: 'Region' }}
          required
          placeholder="Please select"
          disabled={!!regionData || isApiLoading || isConfigLoading}
          withSearch={false}
          value={region_id.value}
          onWppChange={e => {
            handleSelect(e)
            // reset the retailers select when the region changes
            formReducerDispatch(setSelect('retailers', []))
            setRerender(uuidv4())
          }}
          messageType={!region_id.error ? undefined : 'error'}
          message={region_id.error || ''}
        >
          {/* if there is no value in region_id then don't render the flag in the select */}
          {region_id.value && <CircleFlag countryCode={selectedRegion?.flag || 'none'} height={20} slot="icon-start" />}
          {/* if there is no regions in the app render an empty state otherwise render the actual options */}

          {!!regionData ? (
            <WppListItem value={regionData.region_id} data-testid="region-options">
              <CircleFlag slot="left" countryCode={regionData.region_flag || ''} height={22} />
              <p slot="label">{regionData.region_name}</p>
            </WppListItem>
          ) : (
            <>
              {configRegions?.length === 0
                ? renderEmptyState('No regions configured', 'empty-region-option')
                : configRegions?.map(({ _id, name, flag }) => (
                    <WppListItem key={_id} value={_id} data-testid="region-options">
                      <CircleFlag slot="left" countryCode={flag} height={22} />
                      <p slot="label">{name}</p>
                    </WppListItem>
                  ))}
            </>
          )}
        </WppSelect>

        <WppSelect
          data-testid="retailers-select"
          key={rerender}
          name={getType<AddRegionToProjectMultiSelectField>('retailers')}
          labelConfig={{ text: 'Retailers' }}
          placeholder="Please select"
          type="multiple"
          required
          disabled={isApiLoading || isConfigLoading || !region_id.value}
          value={retailers.value}
          onWppChange={e => {
            const { value, name } = e.target
            const typeSafeInputFieldKey = name as AddRegionToProjectMultiSelectField
            formReducerDispatch(setSelect(typeSafeInputFieldKey, value))
            if (formReducerState[typeSafeInputFieldKey].error && !!value)
              formReducerDispatch(handleError(typeSafeInputFieldKey, undefined))
          }}
          messageType={!retailers.error ? undefined : 'error'}
          message={retailers.error || ''}
        >
          {!region_id.value || !retailerOptions?.length
            ? renderEmptyState('No retailers added to this region', 'empty-retailers-option')
            : retailerOptions?.map(retailer => renderSelectItem(retailer?._id!, retailer?.name!, 'retailers-options'))}
        </WppSelect>
      </div>

      <div slot="actions">
        <div className={styles.modal__actionsGroup}>
          <WppButton type="reset" variant="secondary" data-testid="modal-reset-button" disabled={isApiLoading}>
            Cancel
          </WppButton>
          <WppButton
            type="submit"
            variant="primary"
            data-testid="modal-submit-button"
            loading={isApiLoading}
            disabled={isConfigLoading}
          >
            {!!regionData ? 'Save' : 'Add'}
          </WppButton>
        </div>
        {errorMessage && <WppInlineMessage size="s" message={errorMessage} type="error" />}
      </div>
    </WppModal>
  )
}
